import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Seo } from '@/layout/Seo'
import { BlogLayout } from '@/layouts/BlogLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { BlogPost, getPostsByProject } from '@/services/BlogService'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { getProjectTheme } from '@/utils/project-themes'
import { getBasePath } from '@/utils/sitemap/base'
import { loadTranslations } from '@/utils/translate/translate-server'
import BlogView from '@/views/BlogView'

interface BlogProps {
  locale: string
  posts: BlogPost[]
  project: {
    blogMetaDescription?: string
    name: string
    slug: string
  }
}

const BlogPage: NextPageWithLayout<BlogProps> = ({ locale, posts, project }) => {
  const url = `${getBasePath(locale)}${paths.blog.index}/${project.slug}`
  const title =
    project.slug === slugs.angelStudios ? `${project.name} Blog | Angel` : `${project.name} News & Blog | Angel`

  const description =
    project.blogMetaDescription ||
    `Get the latest news about ${project.name}, and dive into the stories behind your favorite characters.`

  const openGraph = { title, description, url, type: 'website' }

  return (
    <>
      <Seo canonical={url} openGraph={openGraph} title={title} description={description} />
      <BlogView posts={posts} project={project.slug} />
    </>
  )
}

BlogPage.getLayout = (page) => {
  return <BlogLayout isDarkMode={false}>{page}</BlogLayout>
}

export const getStaticProps = getStaticPropsErrorHandler<BlogProps>(
  { filePath: 'pages/[locale]/blog/[project]/index.tsx' },
  async ({ locale, next404, params, preview }) => {
    const slug = getItemOrFirstEntry(params?.project)
    if (!slug) return next404()

    const { name } = getProjectTheme(slug)

    const [postsResult, translations] = await Promise.all([
      getPostsByProject({ preview, project: slug }),
      loadTranslations(locale, ['common', 'account']),
    ])
    const { project, posts } = postsResult

    if (posts?.length === 0) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    return {
      props: {
        locale,
        posts: posts ?? [],
        project: { blogMetaDescription: project?.blogMetaDescription, name, slug },
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths = noPreRenderedStaticPaths

export default BlogPage
